// =============================================================================
// Derived calendar entries. Birthdays, public holidays and linked to-dos are
// not stored as events — they are computed from their own rows on every
// change (memoised in Calendar.js, which keeps the dependency lists). The
// label overlays that reserve a colour for those entries and the activity
// feed are built here too. Pure functions: the component owns the memo,
// this file owns the shape.
// =============================================================================

import { format, addMonths, subMonths } from 'date-fns';
import { austrianHolidaysBetween } from '../../lib/holidays';

// Birthdays of the current calendar scope as one entry per year the
// 3-month carousel can touch.
export const buildBirthdayEvents = ({
  rawBirthdays,
  linkedToCalendar,
  categoryId,
  hasBirthdays,
  isSharedView,
  currentDate
}) => {
  if (!linkedToCalendar) return [];
  if (isSharedView && !hasBirthdays) return [];
  // Birthdays repeat every year forever. The carousel only ever renders
  // 3 months (prev / current / next), but those can straddle a year
  // boundary (December ↔ January), so we generate occurrences for every
  // year touched by [prevMonth, nextMonth] — typically 1-2 years. Each
  // occurrence carries its own age label so "(35)" updates as the user
  // scrolls forward into future years.
  const minYear = subMonths(currentDate, 1).getFullYear();
  const maxYear = addMonths(currentDate, 1).getFullYear();
  const out = [];
  for (const p of rawBirthdays) {
    // Normalize legacy format.
    let day = p.day, month = p.month, year = p.year;
    if (!day && p.birthDate) {
      const parts = p.birthDate.split('-').map(Number);
      year = parts[0]; month = parts[1]; day = parts[2];
    }
    if (!day || !month) continue;
    for (let yr = minYear; yr <= maxYear; yr++) {
      // Build via Date so Feb 29 rolls to Mar 1 in non-leap years instead
      // of producing an invalid string that matches no grid cell (the
      // birthday list rolls the same way, keeping both views consistent).
      const dateStr = format(new Date(yr, month - 1, day), 'yyyy-MM-dd');
      const age = year ? yr - year : null;
      const ageLabel = age != null && age > 0 ? ` (${age})` : '';
      out.push({
        id: `birthday-${p.id}-${yr}`,
        title: `${p.name}${ageLabel}`,
        date: dateStr,
        endDate: dateStr,
        allDay: false, // use light-bg style
        startTime: '',
        endTime: '',
        categoryId: categoryId,
        isBirthday: true,
        notes: ''
      });
    }
  }
  return out;
};

// Gesetzliche Feiertage as read-only all-day events, generated for every
// year the 3-month carousel can touch (same reasoning as birthdays).
export const buildHolidayEvents = ({ enabled, categoryId, currentDate, lang }) => {
  if (!enabled) return [];
  const minYear = subMonths(currentDate, 1).getFullYear();
  const maxYear = addMonths(currentDate, 1).getFullYear();
  return austrianHolidaysBetween(minYear, maxYear, lang).map((h) => ({
    id: `holiday-${h.id}-${h.date}`,
    title: h.name,
    date: h.date,
    endDate: h.date,
    allDay: true,
    startTime: '',
    endTime: '',
    categoryId: categoryId || 'red',
    isHoliday: true,
    notes: ''
  }));
};

// To-dos linked into the calendar the user chose (personal or one shared
// calendar). Same pattern as birthdays: the events are derived from the
// task rows rather than stored, so ticking a task off on the Todo page
// immediately crosses it off here too. In a shared view we additionally
// render other members' tasks shared into this calendar (read-only rows
// the loader put into data.todo.sharedTasks).
export const buildTodoEvents = ({
  todoTasks,
  sharedTodoTasks,
  linkedToCalendar,
  categoryId,
  todoSharedCalendarId,
  isSharedView,
  sharedCalendarId
}) => {
  let source;
  if (isSharedView) {
    // Task rows are stamped with their target calendar; the stamp implies
    // the owner's link toggle is on (turning it off restamps to null).
    source = [
      ...todoTasks.filter((task) => task.sharedCalendarId === sharedCalendarId),
      ...sharedTodoTasks.filter((task) => task.sharedCalendarId === sharedCalendarId)
    ];
  } else {
    if (
      !linkedToCalendar ||
      todoSharedCalendarId
    )
      return [];
    source = todoTasks.filter((task) => !task.sharedCalendarId);
  }
  return source
    .filter((task) => task.date)
    .map((task) => ({
      id: `todo-${task.id}`,
      title: task.title,
      date: task.date,
      endDate: task.date,
      allDay: false, // use light-bg style
      startTime: task.time || '',
      endTime: '',
      // Rows carry the owner's chosen color so members see it too;
      // legacy rows fall back to the viewer's own setting.
      categoryId: task.calendarCategoryId || categoryId,
      isTodo: true,
      done: !!task.done,
      priority: task.priority || null,
      notes: task.note || ''
    }));
};

// Which label colour each member's to-dos occupy in THIS calendar. Used to
// show the colour as taken ("Janas To-dos … aus To-dos (Jana)"), the same
// way the birthday label is reserved.
export const buildTodoLabelOwners = ({
  ownTasks,
  foreignTasks,
  settings,
  isSharedView,
  sharedCalendarId,
  userId
}) => {
  const out = new Map(); // categoryId -> userId
  const mine = settings;
  const own = ownTasks;
  const foreign = foreignTasks;
  if (isSharedView) {
    // My own claim first, so my name wins for a colour we both use.
    if (
      mine.linkedToCalendar &&
      mine.sharedCalendarId === sharedCalendarId &&
      mine.categoryId
    ) {
      out.set(mine.categoryId, userId);
    }
    for (const tk of [...own, ...foreign]) {
      if (tk.sharedCalendarId !== sharedCalendarId) continue;
      const cid = tk.calendarCategoryId;
      if (!cid || out.has(cid)) continue;
      out.set(cid, tk.createdBy || userId);
    }
  } else if (mine.linkedToCalendar && !mine.sharedCalendarId && mine.categoryId) {
    out.set(mine.categoryId, userId);
  }
  return out;
};

// Apply birthday-label override: if a category is linked as the birthday label,
// display it as "Birthday" with filled color (treat as a used/custom label).
// Same for the todo label; if both link the same color, birthday wins the
// name (todo events still render in the shared color).
export const applyLabelOverrides = (
  categories,
  {
    birthdayLinked,
    birthdayCategoryId,
    holidaysEnabled,
    holidayCategoryId,
    todoLabelOwners,
    membersById,
    myProfile,
    user,
    t
  }
) => {
  let out = categories;
  if (birthdayLinked) {
    out = out.map((c) =>
      c.id === birthdayCategoryId
        ? { ...c, name: t('birthday.label'), isCustom: true, isBirthdayLabel: true }
        : c
    );
  }
  if (holidaysEnabled) {
    out = out.map((c) =>
      c.id === holidayCategoryId && !c.isBirthdayLabel
        ? { ...c, name: t('calendar.holidaysLabel', 'Feiertage'), isCustom: true, isHolidayLabel: true }
        : c
    );
  }
  if (todoLabelOwners.size > 0) {
    out = out.map((c) => {
      if (!todoLabelOwners.has(c.id) || c.isBirthdayLabel || c.isHolidayLabel) return c;
      const uid = todoLabelOwners.get(c.id);
      const m = uid && membersById[uid] ? membersById[uid] : null;
      const name =
        (m && m.displayName) ||
        (uid && user && uid === user.id && myProfile?.displayName) ||
        '';
      return {
        ...c,
        name: name ? t('calendar.todosOf', '{name}s To-dos', { name }) : t('todo.title'),
        isCustom: true,
        isTodoLabel: true,
        todoOwnerName: name
      };
    });
  }
  return out;
};

// Activity feed: aggregate every audit entry + chat comment from every
// event the user can currently see, grouped by event and sorted with the
// most-recently-active event first.
export const buildActivityFeed = (allCalendarEvents, sharedCalendars) => {
  const sharedById = new Map(
    sharedCalendars.map((c) => [c.id, c])
  );
  const items = [];
  for (const ev of allCalendarEvents) {
    const audit = Array.isArray(ev.audit) ? ev.audit : [];
    const comments = Array.isArray(ev.comments) ? ev.comments : [];
    if (audit.length === 0 && comments.length === 0) continue;
    const entries = [
      ...audit.map((a) => ({ ...a, kind: 'audit' })),
      ...comments.map((c) => ({ ...c, kind: 'comment' }))
    ].sort((a, b) => (b.at || '').localeCompare(a.at || ''));
    const lastAt = entries[0]?.at || '';
    items.push({
      eventId: ev.id,
      event: ev,
      sharedCalendar: ev.sharedCalendarId
        ? sharedById.get(ev.sharedCalendarId) || null
        : null,
      entries,
      lastAt
    });
  }
  items.sort((a, b) => (b.lastAt || '').localeCompare(a.lastAt || ''));
  return items.slice(0, 60);
};
